import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import SignatureModal from "../components/SignatureModal";
import Photo3x4Modal from "../components/Photo3x4Modal";
import ProfileDialog from "../components/ProfileDialog";
import EmployeeMyDocuments from "../components/EmployeeMyDocuments";
import EmployeeAttendanceConvocations from "../components/EmployeeAttendanceConvocations";
import EmployeeCourseTrainings from "../components/EmployeeCourseTrainings";
import EmployeeMyCourses from "../components/EmployeeMyCourses";
import EmployeeCodigoEtica from "../components/EmployeeCodigoEtica";
import EmployeeBannerBlocker from "../components/EmployeeBannerBlocker";
import EmployeeVideoBlocker from "../components/EmployeeVideoBlocker";
import EmployeeReceiptBlocker from "../components/EmployeeReceiptBlocker";
import EmployeeVideos from "../components/EmployeeVideos";
import AdmissionKitWizard from "../components/AdmissionKitWizard";
import WhatsAppSetupModal from "../components/WhatsAppSetupModal";
import MandatorySignaturesBlocker from "../components/MandatorySignaturesBlocker";
import MandatoryCourseBlocker from "../components/MandatoryCourseBlocker";
import FolhaPontoBlocker from "../components/FolhaPontoBlocker";
import CipaSignatureBlocker from "../components/CipaSignatureBlocker";
import QuestionarioBlocker from "../components/QuestionarioBlocker";
import OpsSupervisionView from "../components/OpsSupervisionView";
import {
  FileText,
  LogOut,
  ChevronRight,
  FileSearch,
  User,
  Calendar,
  RefreshCw,
  Megaphone,
  FileSpreadsheet,
  Download,
  Eye,
  Info,
  FolderCheck,
  ArrowLeft,
  Wallet,
  ShieldCheck,
  GraduationCap,
  BookOpen,
  Film,
} from "lucide-react";
import { AboutAppDialog } from "../components/AboutAppDialog";

// View identifiers for the simplified, button-driven dashboard
const VIEW_MENU = "menu";
const VIEW_PAYSLIPS = "payslips";
const VIEW_INCOME = "income";
const VIEW_DOCUMENTS = "documents";
const VIEW_OPS = "ops_supervision";
const VIEW_COURSES = "my_courses";
const VIEW_CODIGO_ETICA = "codigo_etica";
const VIEW_VIDEOS = "videos";

const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout, updateUser } = useAuth();

  // Active view (menu vs. one of the 3 big buttons)
  const [activeView, setActiveView] = useState(VIEW_MENU);

  // Payslip state
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [months, setMonths] = useState([]);
  const [loadingMonths, setLoadingMonths] = useState(false);
  const [loadingYears, setLoadingYears] = useState(false);

  // Income report state
  const [incomeYears, setIncomeYears] = useState([]);
  const [loadingIncomeYears, setLoadingIncomeYears] = useState(false);
  const [selectedIncomeYear, setSelectedIncomeYear] = useState(null);
  const [incomeReportPdf, setIncomeReportPdf] = useState(null);
  const [loadingIncome, setLoadingIncome] = useState(false);

  // Misc UI state
  const [announcement, setAnnouncement] = useState({ text: "", active: false });
  const [signatureChecked, setSignatureChecked] = useState(false);
  const [photo3x4Base64, setPhoto3x4Base64] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [pendingKitCount, setPendingKitCount] = useState(0);
  const [kitChecked, setKitChecked] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  // Track if the employee has at least one issued certificate (controls
  // whether the "Meus Cursos" menu item appears).
  const [hasCertificates, setHasCertificates] = useState(false);
  // Bump this counter to force a full reload of the LMS panels
  // (convocations + course-trainings + certificate count).
  const [refreshTick, setRefreshTick] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const isMounted = useRef(true);
  const hasLoadedYears = useRef(false);
  const hasLoadedIncomeYears = useRef(false);

  // Verify signature + photo 3x4 status from server (handles stale localStorage)
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const checkSignature = async () => {
      try {
        const res = await axios.get(`${API}/employee/signature`, {
          params: { token },
          timeout: 10000,
        });
        if (cancelled) return;
        const serverHas = !!res.data?.has_signature;
        if (serverHas !== !!user?.has_signature) updateUser({ has_signature: serverHas });
      } catch (e) {
        // ignore — blocking modal still shows if user.has_signature is false
      }
      try {
        const res2 = await axios.get(`${API}/employee/photo-3x4`, {
          params: { token },
          timeout: 10000,
        });
        if (cancelled) return;
        const serverHasPhoto = !!res2.data?.has_photo_3x4;
        if (serverHasPhoto !== !!user?.has_photo_3x4) {
          updateUser({ has_photo_3x4: serverHasPhoto });
        }
        if (res2.data?.photo_3x4_base64) {
          setPhoto3x4Base64(res2.data.photo_3x4_base64);
        }
      } catch (e) {
        // ignore — blocking modal still shows if user.has_photo_3x4 is false
      } finally {
        if (!cancelled) setSignatureChecked(true);
      }
    };
    checkSignature();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Check for pending Admission Kit (blocking wizard trigger)
  const checkPendingKits = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/employee/profile`, {
        params: { token },
        timeout: 10000,
      });
      setPendingKitCount(res.data?.pending_admission_kit_count || 0);
    } catch (e) {
      // silent
    } finally {
      setKitChecked(true);
    }
  }, [token]);

  useEffect(() => {
    checkPendingKits();
  }, [checkPendingKits]);

  // Check if the employee has any issued certificates — drives the
  // "Meus Cursos" menu visibility.
  const checkCertificates = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/employee/courses/certificates`, {
        params: { token },
        timeout: 10000,
      });
      setHasCertificates((res.data?.items || []).length > 0);
    } catch (_e) { /* silent */ }
  }, [token]);

  useEffect(() => { checkCertificates(); }, [checkCertificates]);

  // Refresh certificate availability whenever the user returns to the
  // menu (e.g. after just closing the course player with a fresh pass).
  useEffect(() => {
    if (activeView === VIEW_MENU) checkCertificates();
  }, [activeView, checkCertificates]);

  // Re-check certificates whenever the refresh tick changes.
  useEffect(() => {
    if (refreshTick > 0) checkCertificates();
  }, [refreshTick, checkCertificates]);

  const handleManualRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshTick((t) => t + 1);
    // small delay so users see the spinner even on very fast networks
    await new Promise((r) => setTimeout(r, 600));
    setRefreshing(false);
    toast.success("Painel atualizado");
  }, [refreshing]);

  // Fetch announcement
  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const response = await axios.get(`${API}/announcement`, { timeout: 10000 });
        if (response.data && response.data.active) {
          setAnnouncement(response.data);
        }
      } catch (e) {
        // ignore
      }
    };
    fetchAnnouncement();
  }, []);

  // ---- Payslip fetchers (lazy: only when user opens the view) ----
  const fetchYears = useCallback(async () => {
    if (!token) return;
    setLoadingYears(true);
    try {
      const response = await axios.get(`${API}/employee/years`, {
        params: { token },
        timeout: 15000,
      });
      if (!isMounted.current) return;
      const yearsData = response.data || [];
      setYears(yearsData);
      hasLoadedYears.current = true;
      if (yearsData.length > 0 && !selectedYear) {
        setSelectedYear(yearsData[0]);
      }
    } catch (error) {
      if (!isMounted.current) return;
      if (error.response?.status === 401) {
        logout();
        navigate("/");
      } else {
        toast.error("Erro ao carregar contracheques");
      }
    } finally {
      if (isMounted.current) setLoadingYears(false);
    }
  }, [token, logout, navigate, selectedYear]);

  const fetchMonths = useCallback(
    async (year) => {
      if (!token || !year) return;
      setLoadingMonths(true);
      try {
        const response = await axios.get(`${API}/employee/months/${year}`, {
          params: { token },
          timeout: 15000,
        });
        if (!isMounted.current) return;
        setMonths(response.data || []);
      } catch (error) {
        if (!isMounted.current) return;
        if (error.response?.status === 401) {
          logout();
          navigate("/");
        } else {
          setMonths([]);
        }
      } finally {
        if (isMounted.current) setLoadingMonths(false);
      }
    },
    [token, logout, navigate]
  );

  // ---- Income fetchers (lazy) ----
  const fetchIncomeYears = useCallback(async () => {
    if (!token) return;
    setLoadingIncomeYears(true);
    try {
      const response = await axios.get(`${API}/employee/income-report/years`, {
        params: { token },
        timeout: 30000,
      });
      if (!isMounted.current) return;
      const yearsData = response.data || [];
      setIncomeYears(yearsData);
      hasLoadedIncomeYears.current = true;
      if (yearsData.length > 0 && !selectedIncomeYear) {
        setSelectedIncomeYear(yearsData[0]);
      }
    } catch (e) {
      // ignore
    } finally {
      if (isMounted.current) setLoadingIncomeYears(false);
    }
  }, [token, selectedIncomeYear]);

  const fetchIncomeReport = useCallback(
    async (year) => {
      if (!token || !year) return;
      setLoadingIncome(true);
      try {
        const response = await axios.get(`${API}/employee/income-report/${year}`, {
          params: { token },
          timeout: 60000,
        });
        if (!isMounted.current) return;
        setIncomeReportPdf(response.data);
      } catch (error) {
        if (error.response?.status === 404) {
          toast.error("Informe não encontrado para este ano");
        }
        setIncomeReportPdf(null);
      } finally {
        if (isMounted.current) setLoadingIncome(false);
      }
    },
    [token]
  );

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Lazy-load data based on active view
  useEffect(() => {
    if (activeView === VIEW_PAYSLIPS && !hasLoadedYears.current) {
      fetchYears();
    }
    if (activeView === VIEW_INCOME && !hasLoadedIncomeYears.current) {
      fetchIncomeYears();
    }
  }, [activeView, fetchYears, fetchIncomeYears]);

  useEffect(() => {
    if (activeView === VIEW_PAYSLIPS && selectedYear) {
      fetchMonths(selectedYear);
    }
  }, [activeView, selectedYear, fetchMonths]);

  useEffect(() => {
    if (activeView === VIEW_INCOME && selectedIncomeYear) {
      fetchIncomeReport(selectedIncomeYear);
    }
  }, [activeView, selectedIncomeYear, fetchIncomeReport]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleViewPayslip = (payslipId) => {
    navigate(`/payslip/${payslipId}`);
  };

  const openPdfBlob = (base64) => {
    if (!base64) return null;
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: "application/pdf" });
  };

  const handleViewIncomeReport = () => {
    const blob = openPdfBlob(incomeReportPdf?.pdf_base64);
    if (!blob) return;
    window.open(URL.createObjectURL(blob), "_blank");
  };

  const handleDownloadIncomeReport = () => {
    const blob = openPdfBlob(incomeReportPdf?.pdf_base64);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `InformeRendimentos_${selectedIncomeYear}_${user?.matricula || "funcionario"}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Big menu buttons config
  const menuItems = [
    ...(user?.is_supervisor
      ? [
          {
            id: VIEW_OPS,
            title: "Supervisão Operacional",
            subtitle: "Painel de lançamentos e operações",
            icon: ShieldCheck,
            gradient: "from-indigo-700 to-violet-800",
            iconBg: "bg-white/20",
            testId: "menu-btn-ops-supervision",
          },
        ]
      : []),
    {
      id: VIEW_PAYSLIPS,
      title: "Contra Cheque",
      subtitle: "Veja e baixe seus contracheques",
      icon: Wallet,
      gradient: "from-sky-600 to-blue-700",
      iconBg: "bg-white/20",
      testId: "menu-btn-payslips",
    },
    {
      id: VIEW_INCOME,
      title: "Informe de Rendimentos",
      subtitle: "Para sua declaração de Imposto de Renda",
      icon: FileSpreadsheet,
      gradient: "from-emerald-600 to-teal-700",
      iconBg: "bg-white/20",
      testId: "menu-btn-income",
    },
    {
      id: VIEW_DOCUMENTS,
      title: "Meus Documentos Assinados",
      subtitle: "Contratos, EPIs, Avisos e mais",
      icon: FolderCheck,
      gradient: "from-slate-800 to-slate-900",
      iconBg: "bg-white/20",
      testId: "menu-btn-documents",
    },
    ...(hasCertificates
      ? [
          {
            id: VIEW_COURSES,
            title: "Meus Cursos",
            subtitle: "Certificados emitidos e envio por e-mail",
            icon: GraduationCap,
            gradient: "from-amber-500 to-orange-600",
            iconBg: "bg-white/20",
            testId: "menu-btn-my-courses",
          },
        ]
      : []),
    {
      id: VIEW_CODIGO_ETICA,
      title: "Código de Ética e Conduta",
      subtitle: "Documentos institucionais da AVI",
      icon: BookOpen,
      gradient: "from-emerald-600 to-teal-700",
      iconBg: "bg-white/20",
      testId: "menu-btn-codigo-etica",
    },
    {
      id: VIEW_VIDEOS,
      title: "Comunicados em Vídeo",
      subtitle: "Assista aos vídeos publicados pela AVI",
      icon: Film,
      gradient: "from-indigo-600 to-blue-700",
      iconBg: "bg-white/20",
      testId: "menu-btn-videos",
    },
  ];

  // ---------- Renderers for each view ----------
  const renderMenu = () => (
    <motion.div
      key="menu"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2 }}
      className="space-y-4"
      data-testid="employee-menu"
    >
      {/* LMS — pending attendance signatures (OTP) + EAD course trainings.
          Both components hide themselves when there is nothing to show. */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleManualRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-60 transition rounded-md px-2 py-1"
          data-testid="employee-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Atualizando..." : "Atualizar"}
        </button>
      </div>
      <EmployeeAttendanceConvocations
        refreshKey={refreshTick}
        onSigned={() => setRefreshTick((t) => t + 1)}
      />
      <EmployeeCourseTrainings refreshKey={refreshTick} />

      {menuItems.map((item, idx) => {
        const Icon = item.icon;
        return (
          <motion.button
            key={item.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.06 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setActiveView(item.id)}
            data-testid={item.testId}
            className={`w-full text-left bg-gradient-to-br ${item.gradient} rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-shadow active:scale-[0.99]`}
          >
            <div className="flex items-center gap-4">
              <div
                className={`${item.iconBg} w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center flex-shrink-0`}
              >
                <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" strokeWidth={2.2} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-lg sm:text-xl leading-tight tracking-wide">
                  {item.title}
                </p>
                <p className="text-white/80 text-xs sm:text-sm mt-1 leading-snug">
                  {item.subtitle}
                </p>
              </div>
              <ChevronRight className="w-6 h-6 text-white/70 flex-shrink-0" />
            </div>
          </motion.button>
        );
      })}
    </motion.div>
  );

  const renderBackButton = (label) => (
    <Button
      variant="ghost"
      onClick={() => setActiveView(VIEW_MENU)}
      className="mb-4 -ml-2 text-slate-700 hover:bg-slate-100"
      data-testid="back-to-menu-btn"
    >
      <ArrowLeft className="w-4 h-4 mr-2" />
      {label}
    </Button>
  );

  const renderPayslipsView = () => (
    <motion.div
      key="payslips"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
      data-testid="view-payslips"
    >
      {renderBackButton("Voltar ao menu")}

      <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <Wallet className="w-5 h-5 text-sky-600" />
        Contra Cheque
      </h2>

      {loadingYears ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      ) : years.length === 0 ? (
        <div className="text-center py-16">
          <FileSearch className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-base font-semibold text-slate-900 mb-2">
            Nenhum contracheque disponível
          </h3>
          <p className="text-slate-500 text-sm mb-6">
            Seus contracheques aparecerão aqui quando disponíveis.
          </p>
          <Button variant="outline" onClick={fetchYears}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      ) : (
        <>
          {/* Year selector */}
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <Calendar className="w-4 h-4 mr-2 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Ano</span>
            </div>
            <div className="flex space-x-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
              {years.map((year) => (
                <motion.button
                  key={year}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedYear(year)}
                  className={`flex-shrink-0 px-5 py-2.5 rounded-full font-medium text-sm transition-all ${
                    selectedYear === year
                      ? "bg-slate-900 text-white shadow-lg"
                      : "bg-white text-slate-700 border border-slate-200"
                  }`}
                  data-testid={`year-btn-${year}`}
                >
                  {year}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Months list */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <FileText className="w-4 h-4 mr-2 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Contracheques</span>
              </div>
              <div className="flex items-center space-x-2">
                {selectedYear && <span className="text-xs text-slate-500">{selectedYear}</span>}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => selectedYear && fetchMonths(selectedYear)}
                  className="h-7 w-7 p-0"
                  disabled={loadingMonths}
                >
                  <RefreshCw className={`w-3 h-3 ${loadingMonths ? "animate-spin" : ""}`} />
                </Button>
              </div>
            </div>

            {loadingMonths ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div>
              </div>
            ) : months.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                <FileSearch className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Nenhum contracheque em {selectedYear}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {months.map((payslip, index) => {
                  const tipoLabels = {
                    normal: null,  // no badge for normal
                    diferenca: { label: "Diferença Salarial", cls: "bg-amber-50 text-amber-700 border-amber-200" },
                    "13o": { label: "13º Salário", cls: "bg-violet-50 text-violet-700 border-violet-200" },
                    ferias: { label: "Férias", cls: "bg-sky-50 text-sky-700 border-sky-200" },
                    complementar: { label: "Complementar", cls: "bg-slate-100 text-slate-700 border-slate-300" },
                  };
                  const tipoBadge = tipoLabels[payslip.tipo] || null;
                  return (
                  <motion.button
                    key={payslip.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleViewPayslip(payslip.id)}
                    className="w-full p-4 bg-white rounded-xl border border-slate-200 active:bg-slate-50 transition-all text-left"
                    data-testid={`payslip-btn-${payslip.mes}-${payslip.tipo || 'normal'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                          <FileText className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-slate-900 text-base">
                              {payslip.mes_nome}
                            </p>
                            {tipoBadge && (
                              <span
                                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${tipoBadge.cls}`}
                                data-testid={`payslip-tipo-badge-${payslip.mes}`}
                              >
                                {tipoBadge.label}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">
                            {String(payslip.mes).padStart(2, "0")}/{selectedYear}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </motion.button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </motion.div>
  );

  const renderIncomeView = () => (
    <motion.div
      key="income"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
      data-testid="view-income"
    >
      {renderBackButton("Voltar ao menu")}

      <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
        Informe de Rendimentos
      </h2>

      {loadingIncomeYears ? (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-500 text-sm">Carregando...</p>
        </div>
      ) : incomeYears.length === 0 ? (
        <div className="text-center py-16">
          <FileSpreadsheet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-base font-semibold text-slate-900 mb-2">
            Nenhum informe disponível
          </h3>
          <p className="text-slate-500 text-sm mb-6">
            Seus informes de rendimentos aparecerão aqui quando disponíveis.
          </p>
          <Button variant="outline" onClick={fetchIncomeYears}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      ) : (
        <>
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <Calendar className="w-4 h-4 mr-2 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Ano-Calendário</span>
            </div>
            <div className="flex space-x-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
              {incomeYears.map((year) => (
                <motion.button
                  key={year}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedIncomeYear(year)}
                  className={`flex-shrink-0 px-5 py-2.5 rounded-full font-medium text-sm transition-all ${
                    selectedIncomeYear === year
                      ? "bg-slate-900 text-white shadow-lg"
                      : "bg-white text-slate-700 border border-slate-200"
                  }`}
                  data-testid={`income-year-btn-${year}`}
                >
                  {year}
                </motion.button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            {loadingIncome ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto mb-4"></div>
                <p className="text-slate-500 text-sm">Carregando informe...</p>
              </div>
            ) : incomeReportPdf ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
                  <CardContent className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div className="ml-4">
                        <h3 className="font-semibold text-slate-900">Informe de Rendimentos</h3>
                        <p className="text-sm text-slate-500">
                          Ano-Calendário {selectedIncomeYear}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleViewIncomeReport}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        data-testid="view-income-btn"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Visualizar
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleDownloadIncomeReport}
                        className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        data-testid="download-income-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Baixar PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <div className="text-center py-8">
                <FileSearch className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 text-sm">Selecione um ano para ver o informe</p>
              </div>
            )}
          </div>
        </>
      )}
    </motion.div>
  );

  const renderDocumentsView = () => (
    <motion.div
      key="documents"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
      data-testid="view-documents"
    >
      {renderBackButton("Voltar ao menu")}
      <EmployeeMyDocuments />
    </motion.div>
  );

  const renderOpsSupervisionView = () => (
    <OpsSupervisionView
      token={token}
      onBack={() => setActiveView(VIEW_MENU)}
    />
  );

  const renderMyCoursesView = () => (
    <motion.div
      key="my_courses"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
      data-testid="view-my-courses"
    >
      {renderBackButton("Voltar ao menu")}
      <EmployeeMyCourses />
    </motion.div>
  );

  const renderCodigoEticaView = () => (
    <motion.div
      key="codigo_etica"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
      data-testid="view-codigo-etica"
    >
      {renderBackButton("Voltar ao menu")}
      <EmployeeCodigoEtica />
    </motion.div>
  );

  const renderVideosView = () => (
    <motion.div
      key="videos"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
      data-testid="view-videos"
    >
      {renderBackButton("Voltar ao menu")}
      <EmployeeVideos />
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col pb-safe">
      {/* Signature (rúbrica), 3x4 photo and WhatsApp setup are no longer
          required on first login — employees can update these later
          from the profile page. */}

      {/* Admission Kit wizard — first mandatory step after login */}
      <AdmissionKitWizard
        open={kitChecked && pendingKitCount > 0}
        onSigned={() => checkPendingKits()}
      />

      {/* Mandatory document signatures — blocking banner that drops the
          employee on "Meus Documentos" once everything is signed. */}
      <MandatorySignaturesBlocker
        enabled={kitChecked && pendingKitCount === 0}
      />

      {/* Curso EAD obrigatório — bloqueia até assinar + assistir + quiz. */}
      <MandatoryCourseBlocker />

      {/* Folha de ponto — bloqueia até enviar a foto quando convocado. */}
      <FolhaPontoBlocker />

      {/* Assinaturas digitais CIPA pendentes (designado/supervisor). */}
      <CipaSignatureBlocker />

      {/* Questionário psicossocial pendente — assina a lista primeiro,
          depois este blocker cobra as respostas (anônimas). */}
      <QuestionarioBlocker />

      {/* Fullscreen communication banner — the employee must click "Ciente"
          on every active banner before the portal becomes usable. */}
      <EmployeeBannerBlocker />

      {/* Fullscreen communication video — the employee must watch 100% of
          each active video before the CIENTE button is enabled. */}
      <EmployeeVideoBlocker />

      {/* Central de Comunicação — Termos de Aceite dispatched by admin.
          Blocks the entire app until every pending receipt is signed. */}
      <EmployeeReceiptBlocker />

      {/* Profile dialog */}
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 safe-area-top">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden">
                <img src="/logo_full.png" alt="AVI Segurança" className="w-full h-full object-contain" />
              </div>
              <div>
                <h1 className="text-base font-semibold font-heading text-slate-900 tracking-wide">
                  AVI SEGURANÇA
                </h1>
                <p className="text-xs text-slate-500">Painel do Funcionário</p>
              </div>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="h-9 w-9 p-0"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* User Info Card */}
      <div className="px-4 py-4">
        <Card
          role="button"
          tabIndex={0}
          onClick={() => setProfileOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setProfileOpen(true);
            }
          }}
          data-testid="user-info-card"
          className="bg-gradient-to-r from-slate-900 to-slate-800 border-0 text-white cursor-pointer hover:from-slate-800 hover:to-slate-700 transition-colors active:scale-[0.99]"
        >
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center overflow-hidden ring-2 ring-white/30">
                {photo3x4Base64 ? (
                  <img
                    src={photo3x4Base64}
                    alt={user?.name || "Foto do funcionário"}
                    className="w-full h-full object-cover"
                    data-testid="user-info-card-photo"
                  />
                ) : (
                  <User className="w-6 h-6 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="font-semibold text-lg truncate underline-offset-2 group-hover:underline"
                  data-testid="user-name-trigger"
                >
                  {user?.name || "Funcionário"}
                </p>
                <p className="text-slate-300 text-sm font-mono">
                  Matrícula: {user?.matricula || "-"}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        {/* Announcement Banner (fixed at top of content area) */}
        {announcement.active && announcement.text && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg"
            data-testid="announcement-banner"
          >
            <div className="flex items-start space-x-2">
              <Megaphone className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">{announcement.text}</p>
            </div>
          </motion.div>
        )}
      </div>

      {/* Main Content — menu OR active view */}
      <main className="flex-1 px-4 pb-6">
        <AnimatePresence mode="wait">
          {activeView === VIEW_MENU && renderMenu()}
          {activeView === VIEW_PAYSLIPS && renderPayslipsView()}
          {activeView === VIEW_INCOME && renderIncomeView()}
          {activeView === VIEW_DOCUMENTS && renderDocumentsView()}
          {activeView === VIEW_OPS && renderOpsSupervisionView()}
          {activeView === VIEW_COURSES && renderMyCoursesView()}
          {activeView === VIEW_CODIGO_ETICA && renderCodigoEticaView()}
          {activeView === VIEW_VIDEOS && renderVideosView()}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center text-xs text-slate-400 space-y-1">
        <p>DESENVOLVIDO POR DARK HORSE TECNOLOGIA ®</p>
        <button
          type="button"
          onClick={() => setAboutOpen(true)}
          className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-700 text-[11px] tracking-wider underline-offset-2 hover:underline transition"
          data-testid="employee-about-app-btn"
        >
          <Info className="w-3 h-3" />
          SOBRE O APP
        </button>
      </footer>

      <AboutAppDialog open={aboutOpen} onOpenChange={setAboutOpen} />
    </div>
  );
};

export default EmployeeDashboard;
